import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, Group, Progress, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconCircle, IconCircleCheck, IconLock, IconRosette } from '@tabler/icons-react';
import { useOnboarding, type OnboardingStep } from '@/hooks/useOnboarding';
import { STATUS } from '@/lib/statusColors';
import classes from './FirstStepsCard.module.css';

// Онбординг-карточка «Первые шаги» на «Главной»: пошаговый гид обязательных данных с прогрессом,
// аттестация — финальный (мягко заблокированный) шаг. Самоскрывается, когда онбординг не активен
// (не менеджер / агент / организация аттестована / данные не загружены). См.
// docs/pages/home/first-steps-onboarding.md.
export default function FirstStepsCard() {
  const { t } = useTranslation();
  const { active, steps, doneCount, totalCount } = useOnboarding();

  if (!active) {
    return null;
  }

  const percent = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  return (
    <Card withBorder radius="md" padding={0}>
      <Stack gap="sm" p="lg" pb="md">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Group align="center" wrap="nowrap" gap="md">
            <ThemeIcon variant="light" color={STATUS.done} size={44} radius="md">
              <IconRosette size={24} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text fw={600}>{t('app.home.firstSteps.title')}</Text>
              <Text c="gray.7" size="sm">
                {t('app.home.firstSteps.subtitle')}
              </Text>
            </Stack>
          </Group>
          <Text size="sm" c="gray.7" fw={500}>
            {t('app.home.firstSteps.progress', { done: doneCount, total: totalCount })}
          </Text>
        </Group>
        <Progress
          value={percent}
          color={STATUS.done}
          size="sm"
          radius="xl"
          aria-label={t('app.home.firstSteps.progress', { done: doneCount, total: totalCount })}
        />
      </Stack>

      {steps.map((step) => (
        <StepRow key={step.key} step={step} label={t(`app.home.firstSteps.step.${step.key}.label`)} />
      ))}
    </Card>
  );
}

interface StepRowProps {
  readonly step: OnboardingStep;
  readonly label: string;
}

function StepRow({ step, label }: StepRowProps) {
  const { t } = useTranslation();

  if (step.done) {
    return (
      <div className={classes.row}>
        <ThemeIcon variant="light" color={STATUS.done} size="md" radius="xl">
          <IconCircleCheck size={16} />
        </ThemeIcon>
        <Text size="sm" c="gray.6" className={classes.grow}>
          {label}
        </Text>
        <Text size="xs" c={STATUS.done} fw={500}>
          {t('app.home.firstSteps.done')}
        </Text>
      </div>
    );
  }

  if (step.locked) {
    return (
      <div className={classes.row}>
        <ThemeIcon variant="light" color={STATUS.neutral} size="md" radius="xl">
          <IconLock size={16} />
        </ThemeIcon>
        <Text size="sm" c="gray.6" className={classes.grow}>
          {label}
        </Text>
        <Text size="xs" c="gray.5">
          {t('app.home.firstSteps.attestationLocked')}
        </Text>
      </div>
    );
  }

  return (
    <Link to={step.to} className={`${classes.row} ${classes.clickable}`}>
      <ThemeIcon variant="light" color={STATUS.active} size="md" radius="xl">
        <IconCircle size={16} />
      </ThemeIcon>
      <Text size="sm" fw={500} c="dark" className={classes.grow}>
        {label}
      </Text>
      <Text size="sm" fw={500} c="brand.7" style={{ whiteSpace: 'nowrap' }}>
        {t(`app.home.firstSteps.step.${step.key}.cta`)} →
      </Text>
    </Link>
  );
}
